import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  Theme,
} from '@material-ui/core';
import { AddCircle, Close, Search } from '@material-ui/icons';
import React, { ReactNode, useState } from 'react';
import { HighlightingProvider } from '../Highlighting';
import { CommentActions } from './CommentActions';
import { CommentableComment } from './CommentableComment';
import { CommentableSkeleton } from './CommentableSkeleton';
import { EmptyCommentable } from './EmptyCommentable';
import type { MentionableProps } from './Mentionable';
import { useCommentableDraft, useCommentableSearch } from './hooks';
import { Comment, CommentableUser, CommentPayload } from './types';

export interface CommentableProps {
  user: CommentableUser;
  loading: boolean;
  comments: Comment[];
  mentionableProps?: null | Omit<MentionableProps, 'target'>;
  text: {
    editor: {
      submit: ReactNode;
      cancel: ReactNode;
    };
    comments: {
      edit: ReactNode;
      delete: ReactNode;
      dialogTitle: string;
      dialogText: string;
    };
    empty: {
      title: ReactNode;
      description: string;
      action: ReactNode;
    };
    panel: {
      headerTitle: ReactNode;
      headerSearchPlaceholder: string;
    };
  };
  testIds: {
    editor: {
      submitButton: string;
      cancelButton: string;
    };
    comments: {
      card: string;
      kebab: string;
      menu: string;
    };
    empty: {
      addButton: string;
    };
    panel: {
      headerAddButton: string;
      headerSearchInput: string;
    };
    skeleton: string;
  };
  permissions: {
    delete: boolean;
  };
  handlers: {
    addComment(comment: CommentPayload, attributions: string[]): boolean;
    editComment(commentId: string, update: CommentPayload, attributions: string[]): unknown;
    deleteComment(commentId: string): unknown;
    onStartDrafting?(isEdit?: boolean): unknown;
    onStopDrafting?(): unknown;
    onStartNewComment?(): unknown;
  };
  slots?: {
    noSearchResults?: ReactNode;
    commentCardAction?(id: string): ReactNode;
  };
}

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    overflow: 'auto',
  },
  containerCardHeaderActionWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  containerCardHeaderSearchInput: {
    width: theme.spacing(25),
    marginLeft: theme.spacing(1),
  },
  containerCardHeaderSearchInputAddIcon: {
    marginRight: theme.spacing(-2),
  },
  containerCardHeaderSearchInputCloseIcon: {
    cursor: 'pointer',
  },
  containerCardContentWrapper: {
    justifyContent: 'center',
  },
  commentCardHeaderActionWrapper: {
    minWidth: theme.spacing(31),
  },
}));

const COMMENTABLE_SKELETON_COUNT = 3;

export function Commentable(props: CommentableProps) {
  return (
    <HighlightingProvider>
      <CommentableInner {...props} />
    </HighlightingProvider>
  );
}

export function CommentableInner({
  user,
  loading,
  comments,
  text,
  testIds,
  permissions,
  handlers,
  slots,
  mentionableProps,
}: CommentableProps) {
  const classes = useStyles();
  const { query, results, clearQuery, queryChanged } = useCommentableSearch(comments);
  const { drafting, startDrafting, stopDrafting } = useCommentableDraft({
    onStartDrafting: handlers.onStartDrafting,
    onStopDrafting: handlers.onStopDrafting,
  });
  const [editingCommentId, setEditingCommentId] = useState('');
  const isBlankSlate = comments.length === 0 && !loading && !drafting;
  const hasNoResults = query && results.length === 0;

  function createComment(body: string, attributions: string[]) {
    const addedComment = handlers.addComment(
      {
        subject: '',
        body,
      },
      attributions,
    );

    if (addedComment) {
      return stopDrafting();
    }
  }

  function editComment(body: string, attributions: string[]) {
    handlers.editComment(
      editingCommentId,
      {
        subject: '',
        body,
      },
      attributions,
    );

    return stopEditingComment();
  }

  function startEditingComment(commentId: string) {
    setEditingCommentId(commentId);
    handlers.onStartDrafting?.(true);
  }

  function stopEditingComment() {
    setEditingCommentId('');
    handlers.onStopDrafting?.();
  }

  return (
    <Card className={classes.card}>
      <CardHeader
        title={text.panel.headerTitle}
        action={
          <Box className={classes.containerCardHeaderActionWrapper}>
            {/* Always enable adding new comments via the header. */}
            <IconButton
              color="primary"
              onClick={() => {
                startDrafting();
                handlers.onStartNewComment?.();
              }}
              disabled={drafting}
              data-testid={testIds.panel.headerAddButton}
            >
              <AddCircle />
            </IconButton>

            {/* When there are comments to browse, disply an input to filter them. */}
            {!isBlankSlate && comments.length > 0 && (
              <FormControl className={classes.containerCardHeaderSearchInput}>
                <TextField
                  placeholder={text.panel.headerSearchPlaceholder}
                  value={query}
                  onChange={queryChanged}
                  disabled={drafting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment
                        className={classes.containerCardHeaderSearchInputAddIcon}
                        position="end"
                        onClick={clearQuery}
                      >
                        {query && <Close fontSize="small" />}
                      </InputAdornment>
                    ),
                  }}
                  data-testid={testIds.panel.headerSearchInput}
                />
              </FormControl>
            )}
          </Box>
        }
      />
      <Divider />
      <CardContent>
        {/* Prompt the user to create a new comment if none exist and not already creating */}
        {isBlankSlate ? (
          <EmptyCommentable
            title={text.empty.title}
            description={text.empty.description}
            action={{
              title: text.empty.action,
              handler: () => {
                startDrafting();
                handlers.onStartNewComment?.();
              },
              testId: testIds.empty.addButton,
            }}
          />
        ) : (
          <Grid container={true} className={classes.containerCardContentWrapper}>
            {/* Loading state */}
            {loading ? (
              <CommentableSkeleton count={COMMENTABLE_SKELETON_COUNT} testId={testIds.skeleton} />
            ) : (
              <>
                {/* Create a new comment */}
                {drafting && (
                  <Grid item={true} xs={12}>
                    <CommentableComment
                      drafting={drafting}
                      author={user.displayName}
                      text={text.editor}
                      testIds={testIds.editor}
                      handlers={{
                        onSubmit: createComment,
                        onCancel: stopDrafting,
                      }}
                      slots={{
                        commentCardAction: (
                          <Grid className={classes.commentCardHeaderActionWrapper} container={true} alignItems="center">
                            <Grid item={true} xs={9}>
                              {slots?.commentCardAction?.('')}
                            </Grid>
                            <Grid item={true} xs={3}>
                              <CommentActions
                                canEdit={false}
                                canDelete={true}
                                shouldConfirmDelete={false}
                                text={{
                                  ...text.comments,
                                  delete: text.editor.cancel,
                                }}
                                testIds={testIds.comments}
                                handlers={{
                                  edit: () => {
                                    // Pass
                                  },
                                  delete: stopDrafting,
                                }}
                              />
                            </Grid>
                          </Grid>
                        ),
                      }}
                      mentionableProps={mentionableProps}
                    />
                  </Grid>
                )}

                {/* Display all saved comments, sorted by newest first */}
                {[...results]
                  .sort((a, b) => new Date(b.createdTimestamp).getTime() - new Date(a.createdTimestamp).getTime())
                  .map((comment, index) => {
                    const userAuthoredComment = user.id === comment.createdBy.id;
                    const enableMenu = comment.createdBy.id !== 'SYSTEM' && (userAuthoredComment || permissions.delete);

                    return (
                      <Grid key={comment.id} item={true} xs={12} data-testid={`${testIds.comments.card}-${index}`}>
                        <CommentableComment
                          drafting={editingCommentId === comment.id}
                          author={comment.createdBy.displayName}
                          comment={comment}
                          text={text.editor}
                          testIds={testIds.editor}
                          handlers={{
                            onSubmit: editComment,
                            onCancel: stopEditingComment,
                          }}
                          slots={{
                            commentCardAction: (
                              <Grid
                                className={classes.commentCardHeaderActionWrapper}
                                container={true}
                                alignItems="center"
                              >
                                <Grid item={true} xs={9}>
                                  {slots?.commentCardAction?.(comment.id)}
                                </Grid>
                                {enableMenu && (
                                  <Grid item={true} xs={3}>
                                    <CommentActions
                                      canEdit={userAuthoredComment}
                                      canDelete={true}
                                      text={text.comments}
                                      testIds={testIds.comments}
                                      handlers={{
                                        edit: () => startEditingComment(comment.id),
                                        delete: () => handlers.deleteComment(comment.id),
                                      }}
                                    />
                                  </Grid>
                                )}
                              </Grid>
                            ),
                          }}
                          mentionableProps={mentionableProps}
                        />
                      </Grid>
                    );
                  })}

                {hasNoResults && slots?.noSearchResults}
              </>
            )}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}
